// useBrand.js - 品牌配置组合式函数
//
// 用途：
//   在 Vue 组件和 JavaScript 代码中访问品牌配置
//   提供响应式的品牌信息访问
//
// 使用示例（Vue 3 Composition API）：
//   import { useBrand } from '@/composables/useBrand'
//   const { brandName, companyName, logoHtml } = useBrand()
//
// 使用示例（普通 JS）：
//   import { brandConfig } from '@/composables/useBrand'
//   console.log(brandConfig.name)

// 默认配置（作为后备）
const defaultConfig = {
  name: 'METIS Sign',
  company_name: '先飞有限公司',
  company_name_en: 'METIS LIMITED',
  company_full: '先飞有限公司 METIS LIMITED Hong Kong',
  domain: 'metis.so',
  copyright_holder: 'METIS LTD',
  copyright_start_year: 2020,
  logo: {
    text_primary: 'METIS',
    text_secondary: 'Sign',
    color: '#0061FE'
  },
  meta: {
    description: '使用 METIS Sign，您可以随时随地轻松签署文件。立即开始免费试用。',
    keywords: '电子签名,数字签名,文档签署,PDF签名'
  }
}

// 从后端注入的配置中读取，或使用默认配置
export const brandConfig = window.BRAND_CONFIG || defaultConfig

/**
 * 品牌配置组合式函数
 * @returns {Object} 品牌配置对象
 */
export function useBrand() {
  // 基础配置
  const brandName = brandConfig.name
  const companyName = brandConfig.company_name
  const companyNameEn = brandConfig.company_name_en
  const companyFull = brandConfig.company_full
  const domain = brandConfig.domain
  const copyrightHolder = brandConfig.copyright_holder
  const copyrightStartYear = brandConfig.copyright_start_year

  // Logo 配置
  const logoTextPrimary = brandConfig.logo?.text_primary || 'METIS'
  const logoTextSecondary = brandConfig.logo?.text_secondary || 'Sign'
  const logoColor = brandConfig.logo?.color || '#0061FE'

  // 元数据
  const metaDescription = brandConfig.meta?.description
  const metaKeywords = brandConfig.meta?.keywords

  /**
   * 生成 Logo HTML 字符串
   * @param {Object} options - 选项
   * @param {boolean} options.inline - 是否为内联样式（默认 true）
   * @returns {string} HTML 字符串
   */
  const logoHtml = (options = { inline: true }) => {
    if (options.inline) {
      return `${logoTextPrimary} <span style="color: ${logoColor};">${logoTextSecondary}</span>`
    }
    return `${logoTextPrimary} <span class="text-primary">${logoTextSecondary}</span>`
  }

  /**
   * 页面标题
   * @param {boolean} authenticated - 是否已认证
   * @returns {string} 页面标题
   */
  const pageTitle = (authenticated = false) => {
    return authenticated ? brandName : `${brandName} | ${companyFull}`
  }

  /**
   * 版权声明
   * @param {number} year - 年份（可选，默认当前年份）
   * @returns {string} 版权声明文字
   */
  const copyrightNotice = (year = null) => {
    const currentYear = year || new Date().getFullYear()
    return `Copyright © ${copyrightStartYear} - ${currentYear} ${copyrightHolder}. All Rights Reserved. ${companyName} 版权所有`
  }

  /**
   * Webhook User-Agent
   * @returns {string} User-Agent 字符串
   */
  const webhookUserAgent = () => {
    return `${domain.toUpperCase()} Webhook`
  }

  /**
   * 签名原因文字
   * @param {string} signerName - 签署人姓名
   * @returns {string} 签名原因
   */
  const signReason = (signerName) => {
    return `Signed by ${signerName} with ${domain.toUpperCase()}`
  }

  return {
    // 基础配置
    brandName,
    companyName,
    companyNameEn,
    companyFull,
    domain,
    copyrightHolder,
    copyrightStartYear,

    // Logo
    logoTextPrimary,
    logoTextSecondary,
    logoColor,

    // 元数据
    metaDescription,
    metaKeywords,

    // 工具方法
    logoHtml,
    pageTitle,
    copyrightNotice,
    webhookUserAgent,
    signReason,

    // 原始配置对象（供需要访问完整配置的场景）
    config: brandConfig
  }
}

// 默认导出
export default useBrand
